import { useQuery } from "@tanstack/react-query";
import { fetchCondominios } from "@/lib/portal-condominio/queries";
import { fetchCondominioConfig, type CondominioConfig } from "@/lib/portal-condominio/configuracion";
import {
  fetchBitacoraCondominio, fetchCargosCondominio, fetchCondominiosRpc, fetchPagosCondominio, fetchTendenciaCondominio, fetchTesoreriaCondominio,
  fetchUnidadesCondominio, RpcCondominioPendiente,
} from "@/lib/portal-condominio/api";
import type { TendenciaCondominio, UnidadCondominioRpc } from "@/lib/portal-condominio/unidades";
import type { MesTesoreria } from "@/lib/portal-condominio/tesoreria";
import type { EventoBitacora } from "@/lib/portal-condominio/auditoria";
import {
  fetchCambiosTitularidad, fetchHistorialSolicitud, fetchSolicitudesTitularidad, type CambioTitularidad,
  type HistorialSolicitud, type SolicitudTitularidad,
} from "@/lib/portal-condominio/titularidad";
import { type CargoCobranza, type PagoCobranza } from "@/lib/portal-condominio/cobranza";
import { retrySalvoSinPermiso } from "@/lib/rpcErrors";
import type { CondominioRef } from "@/types/condominio";
import { useCondominioImpersonation } from "@/contexts/CondominioImpersonationContext";

/**
 * "Ver como" del Portal Condominio para las lecturas: `verComo` = auth_user_id del impersonado
 * (el servidor lo valida y calcula con su identidad). `listo` es false mientras se resuelve el
 * impersonado: no se consulta con la identidad del admin para no mostrar de más.
 * Fuera del Portal Condominio (Alta Dirección, Cuentas de mantenimiento) no hay provider: null.
 */
export function useCondominioVerComo(): { verComo: string | null; listo: boolean } {
  const { isImpersonating, impersonatedAuthUserId } = useCondominioImpersonation();
  if (!isImpersonating) return { verComo: null, listo: true };
  return { verComo: impersonatedAuthUserId, listo: !!impersonatedAuthUserId };
}

/** No reintentar lo que no se arregla solo: sin permiso o la migración aún sin aplicar. */
const reintentar = (n: number, e: unknown) => !(e instanceof RpcCondominioPendiente) && retrySalvoSinPermiso(n, e);

// Lista de condominios (proyectos con cuentas de mantenimiento activas). Sin la RPC en el
// ambiente se usa la consulta anterior desde el navegador.
export function useCondominios() {
  const { verComo, listo } = useCondominioVerComo();
  return useQuery<CondominioRef[]>({
    queryKey: ["condominios", verComo],
    // Con "ver como" no hay respaldo desde el navegador: la lista la decide el servidor.
    queryFn: async () => (await fetchCondominiosRpc(verComo)) ?? (verComo ? [] : fetchCondominios()),
    enabled: listo,
    staleTime: 30 * 60 * 1000,
  });
}

/** Una fila por cuenta de mantenimiento activa, calculada en el servidor. */
export function useCondominioUnidades(proyectoId: number | null) {
  const { verComo, listo } = useCondominioVerComo();
  return useQuery<UnidadCondominioRpc[]>({
    queryKey: ["condominio-unidades", proyectoId, verComo],
    queryFn: () => fetchUnidadesCondominio(proyectoId as number, verComo),
    enabled: listo && proyectoId != null,
    staleTime: 2 * 60 * 1000,
    retry: reintentar,
  });
}

/** Esperado (mantenimiento y fondo de reserva) contra cobrado, por mes. */
export function useCondominioTendencia(proyectoId: number | null, meses = 12) {
  const { verComo, listo } = useCondominioVerComo();
  return useQuery<TendenciaCondominio[]>({
    queryKey: ["condominio-tendencia", proyectoId, meses, verComo],
    queryFn: () => fetchTendenciaCondominio(proyectoId as number, meses, verComo),
    enabled: listo && proyectoId != null,
    staleTime: 5 * 60 * 1000,
    retry: reintentar,
  });
}

/** Pagos del periodo con su conciliación (Cobranza > Pagos). */
export function useCondominioPagos(proyectoId: number | null, desde: string | null, hasta: string | null, enabled = true) {
  const { verComo, listo } = useCondominioVerComo();
  return useQuery<PagoCobranza[]>({
    queryKey: ["condominio-pagos", proyectoId, desde, hasta, verComo],
    queryFn: () => fetchPagosCondominio(proyectoId as number, desde, hasta, verComo),
    enabled: listo && enabled && proyectoId != null,
    staleTime: 60 * 1000,
    retry: reintentar,
  });
}

/** Cuotas y multas del periodo con su estatus (Cobranza > Cargos). */
export function useCondominioCargos(proyectoId: number | null, desde: string | null, hasta: string | null, enabled = true) {
  const { verComo, listo } = useCondominioVerComo();
  return useQuery<CargoCobranza[]>({
    queryKey: ["condominio-cargos", proyectoId, desde, hasta, verComo],
    queryFn: () => fetchCargosCondominio(proyectoId as number, desde, hasta, verComo),
    enabled: listo && enabled && proyectoId != null,
    staleTime: 60 * 1000,
    retry: reintentar,
  });
}

/** Solicitudes de propietario de las unidades del condominio (Titularidad). */
export function useSolicitudesTitularidad(proyectoId: number | null, idsPropiedad: number[] | null) {
  const ids = [...new Set(idsPropiedad ?? [])].sort((a, b) => a - b);
  return useQuery<SolicitudTitularidad[]>({
    queryKey: ["condominio-titularidad", "solicitudes", proyectoId, ids],
    queryFn: () => fetchSolicitudesTitularidad(ids),
    enabled: proyectoId != null && idsPropiedad != null,
    staleTime: 60 * 1000,
    retry: retrySalvoSinPermiso,
  });
}

/** Cambios de estatus y correos de una solicitud (#1364). Mismo prefijo de clave que el resto de
 * Titularidad: resolver o reasignar lo invalida y se refresca solo. */
export function useHistorialSolicitud(idSolicitud: number | null) {
  return useQuery<HistorialSolicitud | null>({
    queryKey: ["condominio-titularidad", "historial", idSolicitud],
    queryFn: () => fetchHistorialSolicitud(idSolicitud as number),
    enabled: idSolicitud != null,
    staleTime: 30 * 1000,
    retry: retrySalvoSinPermiso,
  });
}

/** Historial de cambios de titular del condominio. */
export function useCambiosTitularidad(proyectoId: number | null) {
  return useQuery<CambioTitularidad[]>({
    queryKey: ["condominio-titularidad", "cambios", proyectoId],
    queryFn: () => fetchCambiosTitularidad(proyectoId as number),
    enabled: proyectoId != null,
    staleTime: 60 * 1000,
    retry: reintentar,
  });
}

/** Ingresos por mes y su aplicación (Tesorería). */
export function useCondominioTesoreria(proyectoId: number | null, meses: number) {
  const { verComo, listo } = useCondominioVerComo();
  return useQuery<MesTesoreria[]>({
    queryKey: ["condominio-tesoreria", proyectoId, meses, verComo],
    queryFn: () => fetchTesoreriaCondominio(proyectoId as number, meses, verComo),
    enabled: listo && proyectoId != null,
    staleTime: 5 * 60 * 1000,
    retry: reintentar,
  });
}

/** Bitácora de eventos del periodo (Auditoría). */
export function useCondominioBitacora(proyectoId: number | null, desde: string | null, hasta: string | null) {
  const { verComo, listo } = useCondominioVerComo();
  return useQuery<EventoBitacora[]>({
    queryKey: ["condominio-bitacora", proyectoId, desde, hasta, verComo],
    queryFn: () => fetchBitacoraCondominio(proyectoId as number, desde, hasta, verComo),
    enabled: listo && proyectoId != null,
    staleTime: 60 * 1000,
    retry: reintentar,
  });
}

// Parámetros de configuración del condominio (proyecto).
export function useCondominioConfig(proyectoId: number | null) {
  return useQuery<CondominioConfig>({
    queryKey: ["condominio-config", proyectoId],
    queryFn: () => fetchCondominioConfig(proyectoId as number),
    enabled: proyectoId != null,
    staleTime: 30 * 60 * 1000,
  });
}
